
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Grid,
} from '@mui/material';
import {
  createRef,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import Dropzone, { FileWithPath } from 'react-dropzone';
import Compressor from 'compressorjs';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { UploadLogoIcon } from '../../vectors/index';
import { Employer } from '@features/superAdmin';
import { toast } from 'react-toastify';
import './cropper.css';
import { track } from '@amplitude/analytics-browser';
import { authContext } from '@staffedupusa/shared-components';
import { LoadingButton } from '@mui/lab';


type PickImageModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  title?: string;
  imageType:
    | 'logo'
    | 'cover_image'
    | 'social_image'
    | 'profilePic'
    | 's_cover_image_file'
    | 'landing_page_images'
    | '';
  minWidth?: number;
  minHeight?: number;
  alertText: string;
  amplitudeEventName?: string;
  setAlertText: Dispatch<SetStateAction<string>>;
  handleUpdateImage: (obj: any, options?: any) => void;
  isUpdatingImage: boolean;
  item?: Employer;
  template_id?: number;
  selectedCompanyId?: string | number;
  multipleImage?: boolean;
};

const PickImageModal: FC<PickImageModalProps> = ({
  isOpen,
  setIsOpen,
  title = 'Upload Photo',
  imageType,
  amplitudeEventName,
  minWidth = 0,
  minHeight = 0,
  alertText,
  setAlertText,
  handleUpdateImage,
  isUpdatingImage,
  item,
  template_id,
  selectedCompanyId,
  multipleImage = false,
}) => {
  const { authData } = useContext(authContext);
  const cropperRef = createRef<ReactCropperElement>();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [currentCroppingIndex, setCurrentCroppingIndex] = useState<number>(-1);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedImage(null);
    setSelectedImages([]);
    setCurrentCroppingIndex(-1);
    setAlertText('');
  };

  const handleDrop = (acceptedFiles: FileWithPath[]) => {
    if (multipleImage) {
      // Handle multiple images
      const validImages: string[] = [];
      let hasInvalidImage = false;

      acceptedFiles.forEach((file) => {
        const image = new Image();
        const imageUrl = URL.createObjectURL(file);
        image.src = imageUrl;

        image.onload = () => {
          if (image.width < minWidth || image.height < minHeight) {
            hasInvalidImage = true;
            setAlertText(
              `One or more images are too small, minimum size is: ${minWidth} × ${minHeight} pixels`,
            );
          } else {
            validImages.push(imageUrl);
            setSelectedImages((prev) => [...prev, imageUrl]);
            if (!hasInvalidImage) {
              setAlertText('');
            }
          }
        };
      });
    } else {
      const selectedFile = acceptedFiles[0];

      if (!selectedFile) {
        return;
      }

      const image = new Image();
      image.src = URL.createObjectURL(selectedFile);

      image.onload = () => {
        if (image.width < minWidth || image.height < minHeight) {
          setAlertText(
            `Image is too small, minimum size is: ${minWidth} × ${minHeight} pixels`,
          );
          setSelectedImage(null);
        } else {
          setAlertText('');
        }
      };

      setSelectedImage(URL.createObjectURL(selectedFile));
    }
  };

  const getCompressedBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve) => {
      new Compressor(blob, {
        quality: 0.8,
        success: (result) => {
          const reader = new FileReader();
          reader.readAsDataURL(result);
          reader.onloadend = () => {
            const base64 = reader.result as string;
            const base64WithoutHeaders = base64.split(',')[1];
            resolve(base64WithoutHeaders);
          };
        },
        error: () => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => resolve(reader.result as string);
        },
      });
    });
  };

  const handleRemoveImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCropImage = (index: number) => {
    setCurrentCroppingIndex(index);
    setSelectedImage(selectedImages[index]);
  };

  const handleSaveCroppedImage = async () => {
    if (!cropperRef.current?.cropper) return;

    const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();

    try {
      const compressedBase64 = await new Promise<string>((resolve) => {
        croppedCanvas.toBlob((blob) => {
          new Compressor(blob!, {
            quality: 0.8,
            success: (result) => {
              const reader = new FileReader();
              reader.readAsDataURL(result);

              reader.onloadend = () => {
                const base64 = reader.result as string;
                const base64WithoutHeaders = base64.split(',')[1];
                resolve(base64WithoutHeaders);
              };
            },
            error: () => resolve(croppedCanvas.toDataURL()),
          });
        });
      });

      // Update the specific image in the array
      setSelectedImages((prev) =>
        prev.map((img, i) =>
          i === currentCroppingIndex
            ? `data:image/png;base64,${compressedBase64}`
            : img,
        ),
      );

      setCurrentCroppingIndex(-1);
      setSelectedImage(null);
    } catch (error) {
      const base64Data = croppedCanvas.toDataURL();
      setSelectedImages((prev) =>
        prev.map((img, i) => (i === currentCroppingIndex ? base64Data : img)),
      );
      setCurrentCroppingIndex(-1);
      setSelectedImage(null);
    }
  };

  const handleSave = async () => {
    if (multipleImage && selectedImages.length > 0) {
      // Handle multiple images save
      const amplitudeEvent = {
        eventProperties: {
          ...(authData?.user && { userId: authData?.user?.id }),
          description: 'The user added multiple images',
        },
      };

      const options = {
        onSuccess: (data: { message: string }) => {
          amplitudeEventName &&
            track(amplitudeEventName, amplitudeEvent.eventProperties);
          toast.success(data.message);
          handleClose();
        },
      };

      // 🔥 Compress each selected image
      const processedImages = await Promise.all(
        selectedImages.map(async (img) => {
          const response = await fetch(img); // convert objectURL to Blob
          const blob = await response.blob();
          return await getCompressedBase64(blob);
        }),
      );

      handleUpdateImage(
        {
          additional_images: processedImages,
          ...(item ? { user_Id: item.id } : {}),
          ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
          ...(item && item.enterprise ? { is_enterprise: 1 } : {}),
          ...(template_id ? { etemplate_id: template_id } : {}),
        },
        options,
      );
    } else if (!multipleImage && cropperRef.current?.cropper) {
      // Handle single image save (existing logic)
      const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();
      const amplitudeEvent = {
        eventProperties: {
          ...(authData?.user && { userId: authData?.user?.id }),
          description:
            amplitudeEventName && amplitudeEventName.includes('logo')
              ? 'The user added a logo'
              : 'The user added a cover',
        },
      };

      const options = {
        onSuccess: (data: { message: string }) => {
          amplitudeEventName &&
            track(amplitudeEventName, amplitudeEvent.eventProperties);
          toast.success(data.message);
          handleClose();
        },
      };

      try {
        const compressedBase64 = await new Promise<string>((resolve) => {
          croppedCanvas.toBlob((blob) => {
            new Compressor(blob!, {
              quality: 0.8,
              success: (result) => {
                const reader = new FileReader();
                reader.readAsDataURL(result);

                reader.onloadend = () => {
                  const base64 = reader.result as string;
                  const base64WithoutHeaders = base64.split(',')[1];
                  resolve(base64WithoutHeaders);
                };
              },
              error: () => resolve(croppedCanvas.toDataURL()),
            });
          });
        });

        handleUpdateImage(
          {
            [imageType]: compressedBase64,
            ...(item ? { user_Id: item.id } : {}),
            ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
            ...(item && item.enterprise ? { is_enterprise: 1 } : {}),
            ...(template_id ? { etemplate_id: template_id } : {}),
          },
          options,
        );
      } catch (error) {
        handleUpdateImage(
          {
            [imageType]: croppedCanvas.toDataURL(),
            ...(item ? { user_Id: item.id } : {}),
            ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
            ...(item && item.enterprise ? { is_enterprise: 1 } : {}),
            ...(template_id ? { etemplate_id: template_id } : {}),
          },
          options,
        );
      }
    }
  };

  const canSave = multipleImage
    ? selectedImages.length > 0 && currentCroppingIndex === -1
    : selectedImage && !alertText;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth={multipleImage ? 'md' : 'xs'}
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <UploadLogoIcon />
        <br />
        <Typography variant="h4" mb={3}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        {selectedImages.length > 3 && (
          <Typography color="error" sx={{ mb: 2, fontWeight: 500 }}>
            You can only upload up to 3 images.
          </Typography>
        )}
        <Stack spacing={3} mb={2}>
          {alertText && <Alert severity="error">{alertText}</Alert>}

          {/* Show cropper when editing a specific image */}
          {currentCroppingIndex !== -1 && selectedImage ? (
            <>
              <Cropper
                movable={false}
                viewMode={1}
                dragMode="crop"
                ref={cropperRef}
                src={selectedImage}
                zoomable={false}
              />
              <Stack direction="row" spacing={2} justifyContent="center">
                <LoadingButton
                  variant="contained"
                  color="success"
                  onClick={handleSaveCroppedImage}
                  disabled={isUpdatingImage}
                  loading={isUpdatingImage}
                >
                  Apply Crop
                </LoadingButton>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCurrentCroppingIndex(-1);
                    setSelectedImage(null);
                  }}
                >
                  Cancel Crop
                </Button>
              </Stack>
            </>
          ) : multipleImage ? (
            /* Multiple images view */
            <>
              <Dropzone
                onDrop={handleDrop}
                accept={{
                  'image/*': ['.png', '.jpeg', '.jpg'],
                }}
                multiple={true}
              >
                {({ getRootProps, getInputProps }) => (
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: '2px dashed #ccc',
                      borderRadius: 5,
                      cursor: 'pointer',
                      padding: 4,
                    }}
                  >
                    <input {...getInputProps()} />
                    <Typography>
                      Drag & drop multiple images or click to add pictures
                    </Typography>
                  </Box>
                )}
              </Dropzone>

              {selectedImages.length > 0 && (
                <Grid container spacing={2}>
                  {selectedImages.map((img, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Box
                        sx={{
                          position: 'relative',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                        }}
                      >
                        <img
                          src={img}
                          alt={`Selected ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '120px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                          }}
                        />
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            borderRadius: 1,
                            p: 0.5,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleCropImage(index)}
                            sx={{ color: 'white', p: 0.5 }}
                          >
                            ✂️
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveImage(index)}
                            sx={{ color: 'white', p: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          ) : (
            /* Single image view (existing logic) */
            <>
              {!selectedImage ? (
                <Dropzone
                  onDrop={handleDrop}
                  accept={{
                    'image/*': ['.png', '.jpeg', '.jpg'],
                  }}
                  maxFiles={1}
                >
                  {({ getRootProps, getInputProps }) => (
                    <Box
                      {...getRootProps()}
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 5,
                        cursor: 'pointer',
                        padding: 4,
                      }}
                    >
                      <input {...getInputProps()} />
                      <Typography>
                        Drag & drop or click to add picture
                      </Typography>
                    </Box>
                  )}
                </Dropzone>
              ) : (
                <Cropper
                  movable={false}
                  viewMode={1}
                  dragMode="crop"
                  ref={cropperRef}
                  src={selectedImage}
                  zoomable={false}
                />
              )}
            </>
          )}

          <Stack direction="row" spacing={2} justifyContent="center">
            {canSave && selectedImages?.length <= 3 && (
              <LoadingButton
                variant="contained"
                color="success"
                onClick={handleSave}
                disabled={isUpdatingImage}
                loading={isUpdatingImage}
              >
                Save
                
              </LoadingButton>
            )}
            <Button
              disabled={isUpdatingImage}
              variant="outlined"
              onClick={handleClose}
              color="secondary"
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PickImageModal;